import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useBudget } from '../../contexts/BudgetContext';
import { useAccount } from '../../contexts/AccountContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import LockedFeature from '../Subscription/LockedFeature';
import api from '../../services/api';

const AISLES = ['produce', 'dairy', 'bakery', 'pantry', 'meat', 'frozen', 'household', 'personal', 'other'];
const UNITS = ['pcs', 'kg', 'l', 'pack'];
const NEXT_STATUS = { have: 'low', low: 'need', need: 'have' };
const STATUS_COLOR = { have: 'success', low: 'warning', need: 'error' };

function catId(c) {
  return c._id || c.id;
}

function itemMeta(item, formatAmount) {
  const bits = [];
  if (item.quantity != null && item.quantity !== '') bits.push(`${item.quantity} ${item.unit || 'pcs'}`);
  if (item.usualPrice) bits.push(formatAmount(item.usualPrice));
  return bits.join(' · ');
}

function PantryEditDialog({ open, item, t, onClose, onSave }) {
  const [form, setForm] = useState({
    name: '', aisle: 'other', status: 'need', quantity: '', unit: 'pcs', usualPrice: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      name: item.name || '',
      aisle: item.aisle || 'other',
      status: item.status || 'need',
      quantity: item.quantity ?? '',
      unit: item.unit || 'pcs',
      usualPrice: item.usualPrice ?? '',
      notes: item.notes || '',
    });
  }, [open, item]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const name = String(form.name || '').trim();
    if (!name) return;
    setSaving(true);
    try {
      await onSave(item.id, {
        name,
        aisle: form.aisle,
        status: form.status,
        unit: form.unit,
        notes: form.notes,
        quantity: form.quantity === '' ? null : Number(form.quantity),
        usualPrice: form.usualPrice === '' ? null : Number(form.usualPrice),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('pantry.editItem', 'Edit item')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={t('pantry.name', 'Name')} value={form.name} onChange={set('name')} fullWidth autoFocus />
          <FormControl fullWidth>
            <InputLabel>{t('pantry.status', 'Status')}</InputLabel>
            <Select label={t('pantry.status', 'Status')} value={form.status} onChange={set('status')}>
              <MenuItem value="have">{t('pantry.have', 'Have')}</MenuItem>
              <MenuItem value="low">{t('pantry.low', 'Low')}</MenuItem>
              <MenuItem value="need">{t('pantry.need', 'Need')}</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label={t('pantry.qty', 'Qty')}
              type="number"
              value={form.quantity}
              onChange={set('quantity')}
              fullWidth
              inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
            />
            <FormControl fullWidth>
              <InputLabel>{t('pantry.unit', 'Unit')}</InputLabel>
              <Select label={t('pantry.unit', 'Unit')} value={form.unit} onChange={set('unit')}>
                {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label={t('pantry.usualPrice', 'Usual price')}
            type="number"
            value={form.usualPrice}
            onChange={set('usualPrice')}
            fullWidth
            inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
          />
          <FormControl fullWidth>
            <InputLabel>{t('pantry.aisle', 'Aisle')}</InputLabel>
            <Select label={t('pantry.aisle', 'Aisle')} value={form.aisle} onChange={set('aisle')}>
              {AISLES.map((a) => (
                <MenuItem key={a} value={a}>{t(`pantry.aisles.${a}`, a)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('pantry.notes', 'Notes')}
            value={form.notes}
            onChange={set('notes')}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('pantry.cancel', 'Cancel')}</Button>
        <Button variant="contained" onClick={save} disabled={saving || !String(form.name).trim()}>
          {saving ? <CircularProgress size={18} /> : t('pantry.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const PantryPage = () => {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const { categories = [] } = useBudget();
  const { accounts = [] } = useAccount();
  const { hasFeature } = useSubscription();
  const unlocked = hasFeature('pantry');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [params, setParams] = useSearchParams();

  const tab = params.get('tab') || 'all';
  const setTab = (v) => {
    const next = new URLSearchParams(params);
    if (v === 'all') next.delete('tab');
    else next.set('tab', v);
    setParams(next, { replace: true });
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quickName, setQuickName] = useState('');
  const [adding, setAdding] = useState(false);
  const [checked, setChecked] = useState({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutAmount, setCheckoutAmount] = useState('');
  const [checkoutCategory, setCheckoutCategory] = useState('');
  const [checkoutAccount, setCheckoutAccount] = useState('');
  const [savingCheckout, setSavingCheckout] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '' });
  const [editing, setEditing] = useState(null);

  const showSnack = (message) => setSnack({ open: true, message });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/pantry');
      setItems(res.data?.data || res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load pantry');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (unlocked) load(); }, [load, unlocked]);

  const visible = useMemo(() => {
    if (tab === 'need') return items.filter((i) => i.status === 'need' || i.status === 'low');
    if (tab === 'have') return items.filter((i) => i.status === 'have');
    return items;
  }, [items, tab]);

  const grouped = useMemo(() => {
    const g = {};
    for (const i of visible) {
      const a = i.aisle || 'other';
      if (!g[a]) g[a] = [];
      g[a].push(i);
    }
    return g;
  }, [visible]);

  const toBuy = items.filter((i) => i.status === 'need' || i.status === 'low');
  const estimate = toBuy.reduce((s, i) => s + (Number(i.usualPrice) || 0), 0);
  const checkedIds = Object.keys(checked).filter((id) => checked[id]);

  const quickAdd = async (e) => {
    e?.preventDefault();
    const name = quickName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await api.post('/pantry', { name, status: 'need' });
      setQuickName('');
      await load();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Could not add');
    } finally {
      setAdding(false);
    }
  };

  const patch = async (id, body) => {
    try {
      const res = await api.patch(`/pantry/${id}`, body);
      const updated = res.data?.data;
      if (updated) {
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...updated } : i)));
      } else {
        await load();
      }
    } catch (err) {
      showSnack(err.response?.data?.message || 'Update failed');
    }
  };

  const cycleStatus = (item) => patch(item.id, { status: NEXT_STATUS[item.status] || 'need' });

  const remove = async (id) => {
    try {
      await api.delete(`/pantry/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      showSnack(err.response?.data?.message || 'Remove failed');
    }
  };

  const markCheckedHave = async () => {
    if (!checkedIds.length) return;
    try {
      await api.post('/pantry/bulk-status', { ids: checkedIds, status: 'have' });
      setChecked({});
      await load();
      showSnack(t('pantry.markedHave', 'Marked as have'));
    } catch (err) {
      showSnack(err.response?.data?.message || 'Update failed');
    }
  };

  const doCheckout = async () => {
    const amount = Number(checkoutAmount);
    if (!(amount > 0)) {
      showSnack(t('pantry.amountRequired', 'Amount must be greater than 0'));
      return;
    }
    setSavingCheckout(true);
    try {
      await api.post('/pantry/checkout', {
        amount,
        categoryId: checkoutCategory || undefined,
        accountId: checkoutAccount || undefined,
        itemIds: checkedIds.length ? checkedIds : toBuy.map((i) => i.id),
      });
      setCheckoutOpen(false);
      setCheckoutAmount('');
      setChecked({});
      await load();
      showSnack(t('pantry.checkedOut', 'Shop saved as an expense'));
    } catch (err) {
      showSnack(err.response?.data?.message || 'Checkout failed');
    } finally {
      setSavingCheckout(false);
    }
  };

  if (!unlocked) {
    return (
      <LockedFeature
        feature="pantry"
        ctaPlan="premium"
        preview="overlay"
        promoText={t('pantry.locked', 'Pantry is a Premium feature — track what you have and what to buy')}
      >
        <Box sx={{ minHeight: 360, borderRadius: 3, bgcolor: 'action.hover' }} />
      </LockedFeature>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      pb: { xs: '96px', md: 4 },
    }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>{t('pantry.title', 'Pantry')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pantry.subtitle', 'What you have at home — and what to buy')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`${toBuy.length} ${t('pantry.toBuy', 'to buy')}`} color={toBuy.length ? 'primary' : 'default'} />
          {estimate > 0 && <Chip label={`~${formatAmount(estimate)}`} variant="outlined" />}
        </Stack>
      </Stack>

      <Box component="form" onSubmit={quickAdd}>
        <TextField
          fullWidth
          placeholder={t('pantry.quickAdd', 'Add something you need…')}
          value={quickName}
          onChange={(e) => setQuickName(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton type="submit" disabled={adding || !quickName.trim()} color="primary">
                  {adding ? <CircularProgress size={18} /> : <AddIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ minHeight: 40, maxWidth: '100%' }}
        >
          <Tab value="all" label={t('pantry.all', 'All')} />
          <Tab value="need" label={t('pantry.toBuy', 'To buy')} />
          <Tab value="have" label={t('pantry.have', 'Have')} />
        </Tabs>
        {tab === 'need' && (
          <Stack direction="row" spacing={1}>
            <Button size="small" disabled={!checkedIds.length} onClick={markCheckedHave}>
              {t('pantry.gotIt', 'Got it')}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<ShoppingCartCheckoutIcon />}
              disabled={!toBuy.length}
              onClick={() => setCheckoutOpen(true)}
            >
              {t('pantry.checkout', 'Checkout')}
            </Button>
          </Stack>
        )}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Alert severity="info">
          {tab === 'need'
            ? t('pantry.emptyNeed', 'Nothing to buy. Add milk, pasta, soap…')
            : t('pantry.empty', 'Your pantry is empty. Add what you keep at home.')}
        </Alert>
      ) : isMobile ? (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          {AISLES.filter((a) => grouped[a]).map((aisle) => (
            <Box key={aisle} sx={{ width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
                {t(`pantry.aisles.${aisle}`, aisle)}
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.5, width: '100%' }}>
                {grouped[aisle].map((item) => (
                  <Box
                    key={item.id || item._id}
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      width: '100%',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{ width: '100%' }}>
                      {tab === 'need' && (
                        <Checkbox
                          size="small"
                          sx={{ ml: -0.75, mt: -0.25 }}
                          checked={Boolean(checked[item.id])}
                          onChange={(e) => setChecked((c) => ({ ...c, [item.id]: e.target.checked }))}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={700} sx={{ wordBreak: 'break-word' }}>{item.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {itemMeta(item, formatAmount) || t(`pantry.aisles.${item.aisle || 'other'}`, item.aisle || 'other')}
                        </Typography>
                        {item.notes ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {item.notes}
                          </Typography>
                        ) : null}
                      </Box>
                      <IconButton size="small" onClick={() => setEditing(item)} aria-label={t('pantry.editItem', 'Edit item')}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => remove(item.id)} aria-label={t('pantry.remove', 'Remove')}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        color={STATUS_COLOR[item.status]}
                        label={t(`pantry.statuses.${item.status}`, item.status)}
                        onClick={() => cycleStatus(item)}
                      />
                      <Chip size="small" variant="outlined" label={t(`pantry.aisles.${item.aisle || 'other'}`, item.aisle || 'other')} />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {tab === 'need' && <TableCell padding="checkbox" />}
              <TableCell>{t('pantry.name', 'Name')}</TableCell>
              <TableCell>{t('pantry.aisle', 'Aisle')}</TableCell>
              <TableCell>{t('pantry.status', 'Status')}</TableCell>
              <TableCell>{t('pantry.qty', 'Qty')}</TableCell>
              <TableCell>{t('pantry.usualPrice', 'Usual price')}</TableCell>
              <TableCell>{t('pantry.notes', 'Notes')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((item) => (
              <TableRow key={item.id} hover>
                {tab === 'need' && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={Boolean(checked[item.id])}
                      onChange={(e) => setChecked((c) => ({ ...c, [item.id]: e.target.checked }))}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Typography fontWeight={700}>{item.name}</Typography>
                </TableCell>
                <TableCell>{t(`pantry.aisles.${item.aisle || 'other'}`, item.aisle || 'other')}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLOR[item.status]}
                    label={t(`pantry.statuses.${item.status}`, item.status)}
                    onClick={() => cycleStatus(item)}
                  />
                </TableCell>
                <TableCell>
                  {item.quantity != null ? `${item.quantity} ${item.unit || 'pcs'}` : '—'}
                </TableCell>
                <TableCell>
                  {item.usualPrice ? formatAmount(item.usualPrice) : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                    {item.notes || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('pantry.editItem', 'Edit item')}>
                    <IconButton size="small" onClick={() => setEditing(item)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('pantry.remove', 'Remove')}>
                    <IconButton size="small" onClick={() => remove(item.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      )}

      <PantryEditDialog
        open={Boolean(editing)}
        item={editing}
        t={t}
        onClose={() => setEditing(null)}
        onSave={patch}
      />

      <Dialog open={checkoutOpen} onClose={() => setCheckoutOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('pantry.checkout', 'Checkout')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('pantry.checkoutHelp', 'Saves one grocery expense and marks selected items as have. Unbought items stay on the list.')}
            </Typography>
            <TextField
              label={t('pantry.amount', 'Amount')}
              type="number"
              value={checkoutAmount}
              onChange={(e) => setCheckoutAmount(e.target.value)}
              required
              inputProps={{ min: 0, step: '0.01' }}
              helperText={estimate > 0 ? `${t('pantry.estimate', 'Estimate')} ~${formatAmount(estimate)}` : ''}
            />
            <FormControl fullWidth>
              <InputLabel>{t('pantry.category', 'Category')}</InputLabel>
              <Select
                label={t('pantry.category', 'Category')}
                value={checkoutCategory}
                onChange={(e) => setCheckoutCategory(e.target.value)}
              >
                <MenuItem value="">{t('pantry.auto', 'Auto')}</MenuItem>
                {(categories || []).map((c) => (
                  <MenuItem key={catId(c)} value={catId(c)}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('pantry.account', 'From account')}</InputLabel>
              <Select
                label={t('pantry.account', 'From account')}
                value={checkoutAccount}
                onChange={(e) => setCheckoutAccount(e.target.value)}
              >
                <MenuItem value="">{t('pantry.none', 'None')}</MenuItem>
                {(accounts || []).map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckoutOpen(false)}>{t('pantry.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={doCheckout} disabled={savingCheckout}>
            {savingCheckout ? <CircularProgress size={18} /> : t('pantry.saveShop', 'Save shop')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default PantryPage;
